import React from "react";
import { Link } from "react-router-dom";
import { Plane, ShieldCheck, Clock, FileCheck2 } from "lucide-react";

/**
 * Two-column premium auth layout.
 * - LEFT: dark navy panel with brand, social proof and trust bullets (desktop only).
 * - RIGHT: white form column, centered, max-width 400px.
 * On mobile the navy panel collapses to a compact header strip.
 */
export function AuthLayout({ children, testId }) {
  return (
    <div data-testid={testId} className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* LEFT panel */}
      <aside className="lg:w-1/2 bg-[#0f1b2d] text-white flex flex-col justify-between px-6 sm:px-10 lg:px-16 py-6 lg:py-16">
        {/* Logo — small on mobile, prominent on desktop */}
        <Link to="/" data-testid="auth-logo-link" className="flex items-center gap-2 self-start">
          <div className="w-9 h-9 rounded-md bg-white text-[#0f1b2d] grid place-items-center">
            <Plane className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className="font-display font-extrabold tracking-tight text-lg">
            Schengen<span className="text-[#60a5fa]">File</span>
          </div>
        </Link>

        {/* Desktop trust content */}
        <div className="hidden lg:block mt-16 max-w-md">
          <div className="text-xs uppercase tracking-[0.24em] text-[#60a5fa] mb-4">
            Trusted by travellers worldwide
          </div>
          <h2 className="font-display font-extrabold text-3xl xl:text-4xl leading-tight">
            14,000+ visa files prepared. <span className="text-[#60a5fa]">4.9 / 5</span> rating.
          </h2>
          <p className="mt-4 text-sm text-white/70 leading-relaxed">
            Book your appointment. We do the paperwork — real reservations, real coverage, delivered fast.
          </p>

          <ul className="mt-10 space-y-4 text-sm">
            <TrustItem icon={Clock} title="Documents ready in 24 hours" />
            <TrustItem icon={FileCheck2} title="Real verifiable reservations" />
            <TrustItem icon={ShieldCheck} title="Secure & private" />
          </ul>
        </div>

        <div className="hidden lg:block text-xs text-white/40 mt-16">
          © {new Date().getFullYear()} SchengenFile · Operated by Trip360 Travel & Tourism Co.
        </div>
      </aside>

      {/* RIGHT form panel */}
      <main className="flex-1 flex items-center justify-center px-6 py-10 lg:py-16 bg-white">
        <div className="w-full max-w-[400px]">{children}</div>
      </main>
    </div>
  );
}

function TrustItem({ icon: Icon, title }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-[#60a5fa]/15 text-[#60a5fa] grid place-items-center flex-shrink-0">
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      </div>
      <div className="pt-1 text-white/90 font-medium">{title}</div>
    </li>
  );
}
